import { gridIndex, type ModelGrid } from '../tiles/grid';

/**
 * One model's wind over the view at the shown time, in its own grid at one level: point (i, j) of `grid` is
 * at index (j − j0)·nx + (i − i0) of `u` and `v` (m/s east and north, NaN where unknown). Columns of grids
 * around the world are unwrapped: i0 may be negative or i0 + nx beyond the grid's width.
 */
export interface WindLayer {
  model: string;
  grid: ModelGrid;
  i0: number;
  j0: number;
  nx: number;
  ny: number;
  u: Float32Array;
  v: Float32Array;
  /** Grid cells over which a regional model fades in from its domain's edge (0 for global models). */
  fade: number;
}

/**
 * What the map draws: the layers of the models shown, coarse to fine. Each finer one covers the ones below
 * where it has data, fading in from its domain's edge, so regional models meet the base without a seam.
 */
export interface WindField {
  layers: readonly WindLayer[];
}

export const EMPTY_FIELD: WindField = { layers: [] };

/**
 * Bilinear wind of a layer at a point into `out` ([u, v]); returns how much it counts (0–1 by the domain
 * edge fade), or 0 where the layer has no data.
 */
export function sampleLayer(l: WindLayer, lng: number, lat: number, out: [number, number]): number {
  const g = l.grid;
  let [x, y] = gridIndex(g, lng, lat);
  if (g.kind === 'latlon' && g.wraps) x -= g.nx * Math.floor((x - l.i0) / g.nx);
  x -= l.i0;
  y -= l.j0;
  if (!(x >= 0 && y >= 0 && x <= l.nx - 1 && y <= l.ny - 1)) return 0;
  const i = Math.min(Math.floor(x), l.nx - 2);
  const j = Math.min(Math.floor(y), l.ny - 2);
  const fx = x - i;
  const fy = y - j;
  const k = j * l.nx + i;
  const { u, v, nx } = l;
  const w00 = (1 - fx) * (1 - fy);
  const w10 = fx * (1 - fy);
  const w01 = (1 - fx) * fy;
  const w11 = fx * fy;
  out[0] = u[k] * w00 + u[k + 1] * w10 + u[k + nx] * w01 + u[k + nx + 1] * w11;
  out[1] = v[k] * w00 + v[k + 1] * w10 + v[k + nx] * w01 + v[k + nx + 1] * w11;
  if (Number.isNaN(out[0]) || Number.isNaN(out[1])) return 0;
  if (l.fade <= 0) return 1;
  // Distance to the domain's edge, in cells of the grid.
  const edge = Math.min(x + l.i0, y + l.j0, g.nx - 1 - (x + l.i0), g.ny - 1 - (y + l.j0));
  return Math.max(0, Math.min(1, edge / l.fade));
}

const scratch: [number, number] = [0, 0];

/**
 * The wind at a point into `out` ([u, v], m/s): each layer blended over the ones below it by its weight.
 * False where no layer has data. Takes an output array: particles sample it thousands of times per frame.
 */
export function sampleField(f: WindField, lng: number, lat: number, out: [number, number]): boolean {
  let known = false;
  for (const layer of f.layers) {
    const w = sampleLayer(layer, lng, lat, scratch);
    if (w <= 0) continue;
    if (!known || w >= 1) {
      out[0] = scratch[0];
      out[1] = scratch[1];
    } else {
      out[0] += (scratch[0] - out[0]) * w;
      out[1] += (scratch[1] - out[1]) * w;
    }
    known = true;
  }
  return known;
}
